import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Search, Filter, Heart, Tent, ArrowRight, Sparkles } from "lucide-react";

/**
 * /share/how-it-works — Simplified step-by-step reel (iter182).
 *
 * Sold as "The only free UK camping compare app". Four numbered steps
 * across 3 screens, ending on a "sign up" push. Anyone who watches
 * this should know what CampCompass does in under 15 seconds.
 */
const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const LINK = `${PUBLIC_URL}/register?ref=how-it-works-reel`;

const STEPS = [
  { icon: Search, title: "1 · Search",  body: "22,000+ UK campsites in one map." },
  { icon: Filter, title: "2 · Filter",  body: "Dogs, EHU, price, region — one tap." },
  { icon: Heart,  title: "3 · Save",    body: "Favourite the ones you love." },
  { icon: Tent,   title: "4 · Book",    body: "Weather + price alerts before you go." },
];

function Scene({ children, bg, label }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500]">{label}</div>
      <section
        className="relative w-[360px] h-[640px] mx-auto flex flex-col justify-between overflow-hidden text-white"
        style={{ background: bg }}
      >
        {children}
      </section>
    </div>
  );
}

export default function HowItWorksReel() {
  return (
    <div className="min-h-screen bg-[#f7f2ec] py-10 px-4">
      <div className="max-w-5xl mx-auto">
        <h1 className="font-display text-4xl mb-2 text-center">How It Works · Reel</h1>
        <p className="text-center text-sm text-[#666] mb-8">15-second walkthrough that ends on signup</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Scene 1 — hook */}
          <Scene label="Scene 1 · Hook" bg="linear-gradient(160deg,#0e0e0e 0%,#2a1a10 100%)">
            <div className="p-6 pt-10">
              <div className="inline-block bg-[#ff4500] text-white text-[10px] px-2 py-1 uppercase tracking-widest font-black mb-6">Free while we grow</div>
              <h2 className="font-display text-[42px] leading-none mb-4">The only<br/><span className="text-[#ff9260]">free</span> UK<br/>camp app.</h2>
              <p className="text-white/85 text-sm">No subscription. No ads. No other service does this.</p>
            </div>
            <div className="p-6 text-[11px] mono uppercase tracking-widest text-white/60 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> here&rsquo;s how it works ↓
            </div>
          </Scene>

          {/* Scene 2 — 4 steps */}
          <Scene label="Scene 2 · 4 steps" bg="#ff4500">
            <div className="p-6 pt-10">
              <h2 className="font-display text-[36px] leading-tight mb-5">Simple<br/>as pitching<br/>a tent.</h2>
              <div className="space-y-2">
                {STEPS.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/15 backdrop-blur-sm px-3 py-2.5">
                    <s.icon className="w-5 h-5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-bold leading-tight">{s.title}</div>
                      <div className="text-[10px] mono uppercase tracking-widest text-white/85">{s.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 text-[11px] mono uppercase tracking-widest text-white/85">60 seconds. Free. That&rsquo;s it.</div>
          </Scene>

          {/* Scene 3 — CTA */}
          <Scene label="Scene 3 · Sign up" bg="linear-gradient(160deg,#0a7f3f 0%,#0e0e0e 100%)">
            <div className="p-6 pt-10">
              <div className="inline-block bg-white text-[#0a7f3f] text-[10px] px-2 py-1 uppercase tracking-widest font-black mb-5">No other service</div>
              <h2 className="font-display text-[40px] leading-none mb-3">Free.<br/>For now.<br/>Forever.</h2>
              <p className="text-white/85 text-sm">Join 22,000+ UK campsites &amp; a real community.</p>
            </div>
            <div className="p-6 flex flex-col items-center gap-2 pb-10">
              <div className="bg-white p-2"><QRCodeSVG value={LINK} size={130} /></div>
              <div className="text-[10px] mono uppercase tracking-widest text-white/70">camp-compass.uk</div>
              <div className="inline-flex items-center gap-1.5 text-sm font-bold mt-1"><ArrowRight className="w-4 h-4" /> Sign up · 60 seconds</div>
            </div>
          </Scene>
        </div>

        <div className="mt-10 p-5 bg-white border hairline max-w-3xl mx-auto">
          <div className="eyebrow mb-2">Caption</div>
          <p className="text-sm">🏕️ The only FREE app that compares 22,000+ UK campsites side-by-side. Search → Filter → Save → Book. That's it. Weather alerts + camper community + partner discounts baked in. No card, no catches. Link in bio ⚡</p>
        </div>
      </div>
    </div>
  );
}
